import { useContext, useEffect, useState } from "react"
import { Form } from "semantic-ui-react"
import { Button } from "../../../designSystems/components/Button"
import { Container } from "./style"
import { RegisterPageContext } from "../../../context/RegisterPageContext"
import { FinancialProps } from "../../../models/FinancialProps"
import { GlobalContext } from "../../../context/GlobalContext"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import { createFinancialContent, getFinancialContent, updateFinancialContent } from "../../../api/financial"
import { annualBudgetOptions, monthlyBillingOptions } from "../../../api/utils/fieldsOptions"

const yesNoOptions = [
    { key: 'none', text: 'Selecione ...', value: '' },
    { key: 1, text: 'Sim', value: 1 },
    { key: 2, text: 'Não', value: 2 },
]

const toSelectValue = (value: number | string | undefined | null) =>
    value === undefined || value === null ? '' : value

const toNumberOrUndefined = (value: unknown): number | undefined => {
    if (value === '' || value === undefined || value === null) return undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
}

export const FinancialContent = (): JSX.Element => {
    const [updateAction, setUpdateAction] = useState(false)

    const { setCurrentPage, handleScroll } = useContext(RegisterPageContext)
    const { setIsLoading, setDataToast, changeLastStep, getErrorMessage, vertifyAuthenticatedRedirect, userId, userData, pathnameIsAdmin, showNextRegisterButton } = useContext(GlobalContext)

    const orgType = userData?.tipo_organizacao_id
    const isOsc = orgType === 1
    const isMei = orgType === 4
    const showFinancialFields = isOsc || isMei

    const { handleSubmit, control, setValue, formState: { errors } } = useForm<FinancialProps>({
        defaultValues: {
            id: undefined,
            annualBudget: undefined,
            monthlyBilling: undefined,
            hasCounter: undefined,
            hasEquip: undefined,
        }
    })

    const navigate = useNavigate()

    useEffect(() => {
        fetchData()
    }, [])

    const fetchData = (): void => {
        setIsLoading(true)

        getFinancialContent(pathnameIsAdmin() ? { user_id: userId } : null).then((response: any) => {
            setIsLoading(false)

            if (response.data.data.length) {
                setUpdateAction(true)
                fillForm(response.data.data[0])
            }
        }).catch(err => {
            setIsLoading(false)
            const textError = err.response?.data?.message === 'Unauthorized'
                ? 'Erro ao buscar informações'
                : getErrorMessage(err.response?.data?.message || 'Erro ao buscar informações')
            setDataToast({ show: true, text: textError })
            if (vertifyAuthenticatedRedirect(err.response?.data?.message || '')) {
                goToPage('/')
            }
        })
    }

    const fillForm = (data: FinancialProps): void => {
        setValue('id', data.id)
        setValue('annualBudget', data.annualBudget)
        setValue('monthlyBilling', data.monthlyBilling)
        setValue('hasCounter', data.hasCounter)
        setValue('hasEquip', data.hasEquip)
    }

    const goToPage = (path: string): void => {
        navigate(path)
    }

    const buildPayload = (data: FinancialProps): FinancialProps => {
        const annualBudget = isOsc
            ? toNumberOrUndefined(data.annualBudget)
            : (toNumberOrUndefined(data.annualBudget) ?? 7)

        const monthlyBilling = isMei
            ? toNumberOrUndefined(data.monthlyBilling)
            : toNumberOrUndefined(data.monthlyBilling)

        return {
            id: data.id,
            annualBudget,
            monthlyBilling,
            hasCounter: toNumberOrUndefined(data.hasCounter),
            hasEquip: toNumberOrUndefined(data.hasEquip),
        }
    }

    const create = (dataForm: FinancialProps): void => {
        setIsLoading(true)
        createFinancialContent(pathnameIsAdmin() ? { user_id: userId } : null, dataForm).then(() => {
            setIsLoading(false)
            setCurrentPage('networks-partners')
            changeLastStep(4)
        }).catch(err => {
            setIsLoading(false)
            handleScroll()
            const apiMessage = err.response?.data?.message
            const textError = apiMessage === 'Unauthorized'
                ? 'Erro ao salvar'
                : getErrorMessage(typeof apiMessage === 'string' ? apiMessage : 'Erro ao salvar sustentabilidade financeira')
            setDataToast({ show: true, text: textError })
            if (vertifyAuthenticatedRedirect(typeof apiMessage === 'string' ? apiMessage : '')) {
                goToPage('/')
            }
        })
    }

    const update = (dataForm: FinancialProps): void => {
        setIsLoading(true)
        updateFinancialContent(dataForm).then(() => {
            setIsLoading(false)
            setCurrentPage('networks-partners')
        }).catch(err => {
            setIsLoading(false)
            handleScroll()
            const apiMessage = err.response?.data?.message
            const textError = apiMessage === 'Unauthorized'
                ? 'Erro ao salvar'
                : getErrorMessage(typeof apiMessage === 'string' ? apiMessage : 'Erro ao salvar sustentabilidade financeira')
            setDataToast({ show: true, text: textError })
            if (vertifyAuthenticatedRedirect(typeof apiMessage === 'string' ? apiMessage : '')) {
                goToPage('/')
            }
        })
    }

    const onSubmit: SubmitHandler<FinancialProps> = data => {
        const payload = buildPayload(data)

        if (isOsc && payload.annualBudget === undefined) {
            setDataToast({ show: true, text: 'Selecione o orçamento anual da iniciativa.' })
            handleScroll()
            return
        }

        if (isMei && payload.monthlyBilling === undefined) {
            setDataToast({ show: true, text: 'Selecione o faturamento mensal da iniciativa.' })
            handleScroll()
            return
        }

        if (payload.hasCounter === undefined || payload.hasEquip === undefined) {
            setDataToast({ show: true, text: 'Preencha todos os campos obrigatórios da sustentabilidade financeira.' })
            handleScroll()
            return
        }

        updateAction ? update(payload) : create(payload)
    }

    return (
        <Container>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <div className="section-header">
                    <h2>Sustentabilidade financeira</h2>
                    <p>
                        Informe os dados financeiros da sua iniciativa. Esses campos ajudam a entender a estrutura
                        e a capacidade de operação no território.
                    </p>
                </div>

                {!showFinancialFields ? (
                    <div className="empty-state">
                        Esta etapa não se aplica ao tipo de organização selecionado. Avance para continuar o cadastro.
                    </div>
                ) : (
                    <div className="row fields-grid">
                        {isOsc && (
                            <div className="col-md-6 field-col-align">
                                <Controller
                                    name="annualBudget"
                                    control={control}
                                    rules={{
                                        required: 'Campo obrigatório',
                                        validate: (value) => (value !== undefined && value !== null && `${value}` !== '') || 'Campo obrigatório',
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                        <>
                                            <Form.Select
                                                fluid
                                                size="huge"
                                                label="Qual é o orçamento anual da sua iniciativa? *"
                                                placeholder="Selecione ..."
                                                className={errors.annualBudget && 'field-error'}
                                                value={toSelectValue(value)}
                                                options={annualBudgetOptions}
                                                selectOnBlur={false}
                                                onChange={(_e, { value: selected }) => onChange(toNumberOrUndefined(selected))}
                                            />
                                            {errors.annualBudget && <p className="field-error-msg">{errors.annualBudget.message || 'Campo obrigatório'}</p>}
                                        </>
                                    )}
                                />
                            </div>
                        )}

                        {isMei && (
                            <div className="col-md-6 field-col-align">
                                <Controller
                                    name="monthlyBilling"
                                    control={control}
                                    rules={{
                                        required: 'Campo obrigatório',
                                        validate: (value) => (value !== undefined && value !== null && `${value}` !== '') || 'Campo obrigatório',
                                    }}
                                    render={({ field: { onChange, value } }) => (
                                        <>
                                            <Form.Select
                                                fluid
                                                size="huge"
                                                label="Qual é o faturamento mensal da sua iniciativa? *"
                                                placeholder="Selecione ..."
                                                className={errors.monthlyBilling && 'field-error'}
                                                value={toSelectValue(value)}
                                                options={monthlyBillingOptions}
                                                selectOnBlur={false}
                                                onChange={(_e, { value: selected }) => onChange(toNumberOrUndefined(selected))}
                                            />
                                            {errors.monthlyBilling && <p className="field-error-msg">{errors.monthlyBilling.message || 'Campo obrigatório'}</p>}
                                        </>
                                    )}
                                />
                            </div>
                        )}

                        <div className="col-md-6 field-col-align">
                            <Controller
                                name="hasCounter"
                                control={control}
                                rules={{
                                    required: 'Campo obrigatório',
                                    validate: (value) => (value !== undefined && value !== null && `${value}` !== '') || 'Campo obrigatório',
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <>
                                        <Form.Select
                                            fluid
                                            size="huge"
                                            label="A iniciativa possui um contador dedicado? *"
                                            placeholder="Selecione ..."
                                            className={errors.hasCounter && 'field-error'}
                                            value={toSelectValue(value)}
                                            options={yesNoOptions}
                                            selectOnBlur={false}
                                            onChange={(_e, { value: selected }) => onChange(toNumberOrUndefined(selected))}
                                        />
                                        {errors.hasCounter && <p className="field-error-msg">{errors.hasCounter.message || 'Campo obrigatório'}</p>}
                                    </>
                                )}
                            />
                        </div>

                        <div className="col-md-6 field-col-align">
                            <Controller
                                name="hasEquip"
                                control={control}
                                rules={{
                                    required: 'Campo obrigatório',
                                    validate: (value) => (value !== undefined && value !== null && `${value}` !== '') || 'Campo obrigatório',
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <>
                                        <Form.Select
                                            fluid
                                            size="huge"
                                            label={isOsc
                                                ? 'A iniciativa tem equipe dedicada para captação de recursos? *'
                                                : 'A iniciativa tem equipe dedicada para vendas/captação? *'}
                                            placeholder="Selecione ..."
                                            className={errors.hasEquip && 'field-error'}
                                            value={toSelectValue(value)}
                                            options={yesNoOptions}
                                            selectOnBlur={false}
                                            onChange={(_e, { value: selected }) => onChange(toNumberOrUndefined(selected))}
                                        />
                                        {errors.hasEquip && <p className="field-error-msg">{errors.hasEquip.message || 'Campo obrigatório'}</p>}
                                    </>
                                )}
                            />
                        </div>
                    </div>
                )}

                <div className="footer">
                    <hr />
                    <div className="btn-actions f-right">
                        <Button
                            text="Anterior"
                            type="button"
                            className="radius-100 btn-outline-gray mr-32"
                            onClick={() => setCurrentPage('about-initiative')}
                        />
                        {showNextRegisterButton(updateAction) && (
                            <Button
                                type="submit"
                                text="Próxima"
                                className="radius-100"
                                onClick={() => Object.keys(errors).length && handleScroll()}
                            />
                        )}
                        {!showFinancialFields && (
                            <Button
                                type="button"
                                text="Próxima"
                                className="radius-100"
                                onClick={() => {
                                    setCurrentPage('networks-partners')
                                    changeLastStep(4)
                                }}
                            />
                        )}
                    </div>
                </div>
            </Form>
        </Container>
    )
}
